import type { WorkflowConfiguration, StageConfiguration, FieldConfiguration } from "@/types/workflow"

export interface ResolvedAuditData {
  stageTitle: string
  fieldDisplayName: string
  actionDisplayName: string
}

/**
 * Resolves the display name for a stage code from workflow configurations
 */
export function resolveStageTitle(
  stageCode: string,
  configuration: WorkflowConfiguration | undefined,
  language: 'en' | 'fr' = 'en'
): string {
  if (!configuration?.stageConfigurations) {
    return stageCode
  }

  const stageConfig = configuration.stageConfigurations.find(
    stage => stage.stageCode === stageCode
  )

  if (!stageConfig) {
    return stageCode
  }

  const title = language === 'fr' ? stageConfig.titleFrench : stageConfig.titleEnglish
  return title
}

/**
 * Resolves the display name for a field from workflow configurations
 * Returns null if the field is not found in the configuration (should be hidden)
 */
export function resolveFieldDisplayName(
  fieldName: string,
  configuration: WorkflowConfiguration | undefined,
  language: 'en' | 'fr' = 'en',
  propertyPath?: string
): string | null {
  if (!configuration?.fieldsInUse) {
    return null
  }

  // If fieldName is just a number (array index), prioritize propertyPath matching
  if (propertyPath && /^\d+$/.test(fieldName)) {
    // Try to match by propertyPath first since fieldName is just an index (case insensitive)
    let fieldConfig = configuration.fieldsInUse.find(
      field => field.columnCode && field.columnCode.toLowerCase() === propertyPath.toLowerCase()
    )
    
    if (fieldConfig) {
      const displayName = language === 'fr' ? fieldConfig.columnNameFrench : fieldConfig.columnNameEnglish
      return displayName
    }
  }

  // First try to match by exact columnCode (case insensitive)
  let fieldConfig = configuration.fieldsInUse.find(
    field => field.columnCode && field.columnCode.toLowerCase() === fieldName.toLowerCase()
  )

  // If not found, try to match by PropertyPath (case insensitive)
  if (!fieldConfig && propertyPath) {
    fieldConfig = configuration.fieldsInUse.find(
      field => field.columnCode && field.columnCode.toLowerCase() === propertyPath.toLowerCase()
    )
  }

  if (!fieldConfig) {
    return null
  }

  const displayName = language === 'fr' ? fieldConfig.columnNameFrench : fieldConfig.columnNameEnglish
  return displayName
}

/**
 * Resolves the display name for an action
 */
export function resolveActionDisplayName(action: string): string {
  const actionMap: Record<string, string> = {
    'Create': 'Created',
    'Update': 'Updated',
    'Edit': 'Edited',
    'Delete': 'Deleted',
    'StatusChange': 'Status Changed',
    'Assignment': 'Assigned',
    'Unassignment': 'Unassigned'
  }

  return actionMap[action] || action
}

/**
 * Formats a value for display in the audit history
 */
export function formatAuditValue(value: any): string {
  if (value === null || value === undefined) return '—'
  
  // Handle boolean values
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  
  // Handle numbers
  if (typeof value === 'number') {
    return value.toString()
  }
  
  // Handle strings
  if (typeof value === 'string') {
    if (value.trim() === '') return '—'
    if (value.toLowerCase() === 'true') return 'Yes'
    if (value.toLowerCase() === 'false') return 'No'
    return value
  }
  
  // Handle other types (objects, arrays, etc.)
  return String(value)
}

/**
 * Gets the appropriate icon for an action type
 */
export function getActionIcon(action: string): string {
  const actionIconMap: Record<string, string> = {
    'Create': 'Plus',
    'Update': 'Edit',
    'Delete': 'Trash2',
    'StatusChange': 'RefreshCw',
    'Assignment': 'UserPlus',
    'Unassignment': 'UserMinus'
  }

  return actionIconMap[action] || 'Edit'
}
